/* The undo history for care-plan questionnaire answer decisions.
 *
 * It lives OUTSIDE React because the surface that makes the decisions does not
 * survive them: accepting an atom settles it, so the card unmounts, and the
 * whole questionnaire block remounts on a reveal toggle or an editor re-render.
 * A stack held in component state would be gone by the time the clinician
 * reached for Cmd+Z. A module-level store also lets one stack span every
 * questionnaire on the page, which is what makes interleaved decisions across
 * different items undo in the order they were made.
 *
 * The stack is deliberately tab-scoped and not persisted: the DURABLE record of
 * a decision is the staging `Basic`, and an undo history that outlived a reload
 * would offer to reverse writes whose surrounding answer has since moved on.
 *
 * Performing an undo needs a FHIR write and the staging projections, which only
 * the hook rendering that questionnaire can do — so the entries here are plain
 * data and each questionnaire registers an executor that knows how to apply
 * them. An entry whose surface has unmounted is simply not undoable until it
 * mounts again.
 *
 * That same registration carries the editor's "Accept all" and its reversal.
 * They keep no entry here — a sweep is one action and is undone in one press
 * from the editor's own history — but they need the identical thing the
 * reversals need: the mounted questionnaire that can write the response. */

import type { AnswerScaffoldLabel } from '@seen/core/models/care-plans/answerScaffold'
import type { PersistedQuestionnaireAnswerOp } from '@seen/core/models/compositionNote'
import { create } from 'zustand'
import type {
  BulkAcceptItemWrite,
  BulkAcceptResult,
} from '#src/components/care-plan/answerAccept.ts'
import type { AtomAnswerSnapshot } from '#src/components/care-plan/answerUndo.ts'

type SuggestedAnswer = PersistedQuestionnaireAnswerOp['answers'][number]

interface DecidedAtom {
  /* Identity that survives the entry moving between the two stacks, so a
   * reversal commits the entry it actually applied rather than whatever sits on
   * top by the time the compensating write lands. */
  seq: number
  questionnaireUrl: string
  /* The atom is addressed by KEY within its questionnaire, never by the staging
   * record it was decided against. A later AI run supersedes that record and
   * carries its atoms into the fresh one, so a captured record id can name a
   * record that no longer exists — and writing to it does nothing, which is how
   * an undo came to revert an answer and leave no card to accept it again. The
   * key survives the move; the record holding it is resolved at reversal time.
   * The record-level status is re-derived then too, rather than replayed from
   * whatever the original decision settled, because the record it lands on may
   * hold atoms from another run by then. */
  atomKey: string
  answer: SuggestedAnswer
}

export interface AcceptedAtomEntry extends DecidedAtom {
  kind: 'accept'
  // The answer as the accept found it, and as the accept left it.
  before: AtomAnswerSnapshot
  after: AtomAnswerSnapshot
}

export interface RejectedAtomEntry extends DecidedAtom {
  kind: 'reject'
}

export type QuestionnaireDecisionEntry = AcceptedAtomEntry | RejectedAtomEntry

export type QuestionnaireDecisionInput =
  | Omit<AcceptedAtomEntry, 'seq'>
  | Omit<RejectedAtomEntry, 'seq'>

export interface QuestionnaireDecisionUndoExecutor {
  undo: (entry: QuestionnaireDecisionEntry) => void
  redo: (entry: QuestionnaireDecisionEntry) => void
  /* The bulk half, reached by the editor's "Accept all". It sits on the same
   * registration as the per-atom reversals because it needs exactly the same
   * thing they do — a mounted questionnaire, which is the only place the
   * QuestionnaireResponse can be written — and so shares their mount lifetime
   * and their "later mount owns the slot" rule.
   *
   * Both report through a callback rather than returning, because each is one
   * PATCH: `acceptAll` reports the writes it made so the batch can be
   * compensated for, along with the atoms it could not place, without which the
   * caller cannot tell a finished record from one still holding a decision; and
   * `reverseBulkAccept` reports the linkIds it managed to take back, which need
   * not be all of them. */
  acceptAll: (
    answers: readonly SuggestedAnswer[],
    onDone: (result: BulkAcceptResult) => void
  ) => void
  reverseBulkAccept: (
    writes: readonly BulkAcceptItemWrite[],
    onDone: (reversedLinkIds: string[]) => void
  ) => void
  /* Which of those writes a reversal would still accept, asked BEFORE anything
   * is written. Pure — `planBulkAcceptReversal` judges the answer as it stands —
   * so the bulk undo can decide the whole batch first and never re-pend an atom
   * whose value it already knows it cannot take back. */
  reversibleLinkIds: (writes: readonly BulkAcceptItemWrite[]) => string[]
  /* The scaffold lines this questionnaire's CURRENT answers offer, per item.
   * Sits on the same registration as the writes for the same reason they do —
   * only the mounted questionnaire holds the live answers — and is asked by the
   * staging handler, which runs in the chat widget, outside this surface's React
   * tree and with no other route to them. Undefined for an item with no answer
   * yet; the handler refuses a label the mounted questionnaire cannot place. */
  scaffoldLabels: (linkId: string) => AnswerScaffoldLabel[] | undefined
}

interface QuestionnaireDecisionUndoState {
  // Reversible decisions, most recent last.
  undoStack: QuestionnaireDecisionEntry[]
  // Reversed decisions available to re-apply, most recent last.
  redoStack: QuestionnaireDecisionEntry[]
  recordDecision: (input: QuestionnaireDecisionInput) => void
  // Moves a reversed entry to the redo stack. Called only once the compensating
  // write has landed, so a refused or failed undo leaves the entry reversible.
  commitUndo: (seq: number) => void
  commitRedo: (seq: number) => void
  resetDecisionUndo: () => void
}

let nextSeq = 1

/* Seqs whose reversal is mid-flight. An undo's staging write and its
 * compensating answer patch are asynchronous, and the entry deliberately stays
 * on the stack until both have landed so a failed one is still reversible —
 * which leaves a window where a second Cmd+Z dispatches the SAME entry again.
 * The two reversals then race, and if the first fails while the second
 * succeeds, the first's compensation puts the decision back on an atom the
 * second has already taken out of the answer: the value gone from the plan with
 * no card left to put it back. */
const reversalsInFlight = new Set<number>()

// True when this call owns the entry's reversal; false when one is already
// running for it, which the caller answers by doing nothing.
export function claimDecisionReversal(seq: number): boolean {
  if (reversalsInFlight.has(seq)) {
    return false
  }
  reversalsInFlight.add(seq)
  return true
}

export function releaseDecisionReversal(seq: number): void {
  reversalsInFlight.delete(seq)
}

export const useQuestionnaireDecisionUndoStore =
  create<QuestionnaireDecisionUndoState>((set) => ({
    undoStack: [],
    redoStack: [],
    recordDecision: (input) => {
      const seq = nextSeq
      nextSeq += 1
      set((state) => ({
        undoStack: [...state.undoStack, { ...input, seq }],
        // A fresh decision branches off any redo history, exactly as a forward
        // edit does in an ordinary undo stack.
        redoStack: [],
      }))
    },
    commitUndo: (seq) =>
      set((state) => {
        const entry = state.undoStack.find((e) => e.seq === seq)
        if (!entry) {
          return state
        }
        return {
          undoStack: state.undoStack.filter((e) => e.seq !== seq),
          redoStack: [...state.redoStack, entry],
        }
      }),
    commitRedo: (seq) =>
      set((state) => {
        const entry = state.redoStack.find((e) => e.seq === seq)
        if (!entry) {
          return state
        }
        return {
          redoStack: state.redoStack.filter((e) => e.seq !== seq),
          undoStack: [...state.undoStack, entry],
        }
      }),
    resetDecisionUndo: () => {
      reversalsInFlight.clear()
      set({ undoStack: [], redoStack: [] })
    },
  }))

const executors = new Map<string, QuestionnaireDecisionUndoExecutor>()

export function registerQuestionnaireDecisionExecutor(
  questionnaireUrl: string,
  executor: QuestionnaireDecisionUndoExecutor
): () => void {
  executors.set(questionnaireUrl, executor)
  return () => {
    // Two surfaces can render the same questionnaire (the summary panel and the
    // care-plan node view); the later mount owns the slot, so an earlier one
    // unmounting must not clear it.
    if (executors.get(questionnaireUrl) === executor) {
      executors.delete(questionnaireUrl)
    }
  }
}

function runOn(
  entry: QuestionnaireDecisionEntry | undefined,
  apply: (
    executor: QuestionnaireDecisionUndoExecutor,
    entry: QuestionnaireDecisionEntry
  ) => void
): boolean {
  if (!entry) {
    return false
  }
  const executor = executors.get(entry.questionnaireUrl)
  if (!executor) {
    return false
  }
  apply(executor, entry)
  return true
}

/* Reverse the most recent decision. True once an executor has taken it on —
 * including when that executor goes on to refuse the write, because the key
 * press has been answered either way. False when there is nothing to reverse,
 * or when the questionnaire that owns it is no longer mounted, so the shortcut
 * falls through untouched rather than reaching for something unrelated. */
export function undoQuestionnaireDecision(): boolean {
  const { undoStack } = useQuestionnaireDecisionUndoStore.getState()
  return runOn(undoStack.at(-1), (executor, entry) => executor.undo(entry))
}

export function redoQuestionnaireDecision(): boolean {
  const { redoStack } = useQuestionnaireDecisionUndoStore.getState()
  return runOn(redoStack.at(-1), (executor, entry) => executor.redo(entry))
}

/* Write every listed answer of one questionnaire in a single patch. False when
 * no questionnaire is mounted for that URL, so a sweep leaves those answers
 * undecided rather than marking them accepted with nothing written. */
export function acceptAllQuestionnaireAnswers(
  questionnaireUrl: string,
  answers: readonly SuggestedAnswer[],
  onDone: (result: BulkAcceptResult) => void
): boolean {
  const executor = executors.get(questionnaireUrl)
  if (!executor) {
    return false
  }
  executor.acceptAll(answers, onDone)
  return true
}

/* The labels a mounted questionnaire can place onto, for one item. An empty
 * array is a real answer offering no scaffold line; `undefined` means no
 * questionnaire is mounted for that url, which is not evidence of anything and
 * the caller must not read as a refusal. */
export function questionnaireScaffoldLabels(
  questionnaireUrl: string,
  linkId: string
): AnswerScaffoldLabel[] | undefined {
  return executors.get(questionnaireUrl)?.scaffoldLabels(linkId)
}

/* The subset of a sweep's writes that can still be reversed. An empty array
 * when no questionnaire is mounted for that url, which is also the honest
 * answer: nothing can be taken back through a surface that is not there. */
export function reversibleBulkAcceptLinkIds(
  questionnaireUrl: string,
  writes: readonly BulkAcceptItemWrite[]
): string[] {
  return executors.get(questionnaireUrl)?.reversibleLinkIds(writes) ?? []
}

/* Un-write what one such sweep wrote. False on the same condition, and the
 * reported linkIds may be a subset even when it is true — an item whose answer
 * has moved since is refused rather than overwritten. */
export function reverseQuestionnaireBulkAccept(
  questionnaireUrl: string,
  writes: readonly BulkAcceptItemWrite[],
  onDone: (reversedLinkIds: string[]) => void
): boolean {
  const executor = executors.get(questionnaireUrl)
  if (!executor) {
    return false
  }
  executor.reverseBulkAccept(writes, onDone)
  return true
}
